import React from "react";
import { AccentColor } from "../../types/branding.types";

export interface GlowingIconProps {
  icon: React.ReactNode;
  className?: string;
  color?: AccentColor;
  size?: "sm" | "md" | "lg" | "xl";
  animate?: boolean;
}

/**
 * GlowingIcon Component
 *
 * Wrapper component that adds neon glow effect to icons.
 * Supports multiple color variants and sizes.
 *
 * @example
 * import { Shield } from 'lucide-react';
 *
 * <GlowingIcon icon={<Shield />} color="purple" size="lg" animate />
 */
export const GlowingIcon: React.FC<GlowingIconProps> = ({
  icon,
  className = "",
  color = "main",
  size = "md",
  animate = false,
}) => {
  const colorClasses = {
    main: "text-wizard-accent-main glow-main",
    fraud: "text-wizard-accent-fraud glow-fraud",
    streaming: "text-wizard-accent-streaming glow-streaming",
    radio: "text-wizard-accent-radio glow-radio",
    omen: "text-wizard-accent-omen glow-omen",
    station: "text-wizard-accent-station glow-station",
    cvplus: "text-wizard-accent-cvplus glow-cvplus",
    claudette: "text-wizard-accent-main glow-main",
    bayit: "text-wizard-accent-streaming glow-streaming",
    olorin: "text-wizard-accent-main glow-main",
    purple: "text-wizard-accent-purple glow-purple", // Backward compatibility
  };

  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-24 h-24",
  };

  const animateClass = animate ? "glow-pulse" : "";

  const iconClass = `glow-icon ${colorClasses[color]} ${sizeClasses[size]} ${animateClass} ${className}`;

  return <div className={iconClass}>{icon}</div>;
};

export default GlowingIcon;
